import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { SignatureDto } from "serverApi/vehicleSubjectChange";
import { vehicleDigiSignApi } from "serverApi";
import { SubjectTypeEnum } from "types";

import { DigiSignStatus } from "../types/VehicleSubjectChangeFormValues";

export type DigisignEligibleResponse = {
    isEligible: boolean;
    subjectType?: SubjectTypeEnum;
    phoneNumber?: string;
    email?: string;
};

export default function useDigisign(idObject, idChange) {
    const [isDigisignEligibilityLoading, setIsDigisignEligibilityLoading] = useState(false);
    const [digisignEligibilityData, setDigisignEligibilityData] = useState<DigisignEligibleResponse>({ isEligible: false });

    const [isSigning, setIsSigning] = useState(false);
    const [isSignInitializationFailed, setIsSignInitializationFailed] = useState(false);

    const [digitalSignatureOnlineStatus, setDigitalSignatureOnlineStatus] = useState<DigiSignStatus>("idle");

    const [searchParams, setSearchParams] = useSearchParams();

    const checkDigisignEligibility = async () => {
        setIsDigisignEligibilityLoading(true);

        const [error, res] = await vehicleDigiSignApi.checkDigisignEligibilityNoParams();

        if (error) {
            setDigisignEligibilityData({ isEligible: false });
            setIsDigisignEligibilityLoading(false);
            return;
        }

        if (res.ok && res.body) {
            const response: DigisignEligibleResponse = res.body;

            const eligibilityData = {
                isEligible: response?.isEligible === true,
                subjectType:
                    response?.subjectType?.toUpperCase() === SubjectTypeEnum.COMPANY ? SubjectTypeEnum.COMPANY : SubjectTypeEnum.INDIVIDUAL,
                phoneNumber: response?.phoneNumber,
                email: response?.email,
            };

            setDigisignEligibilityData(eligibilityData);
            setIsDigisignEligibilityLoading(false);
        }
    };

    const signDocument = async () => {
        setIsSigning(true);
        setIsSignInitializationFailed(false);

        const dto: SignatureDto = {
            idObject: idObject,
            redirectUrl: window.location.href,
        };

        const [error, res] = await vehicleDigiSignApi.sign(idChange, dto);

        if (error) {
            setIsSignInitializationFailed(true);
            setIsSigning(false);
            return;
        }

        if (res.ok && res.body) {
            if (!res) return;
            const redirectUrl = res.body.signRedirectUrl;
            window.location.href = redirectUrl;
        }
    };

    async function delay(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    async function trySignWithRetries(changeSheetId: string, envelopeId: string, maxAttempts = 3, delayMs = 5000) {
        setDigitalSignatureOnlineStatus("loading");
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const [error, res] = await vehicleDigiSignApi.finalize(changeSheetId, envelopeId);
                if (error) {
                    throw error;
                }
                setDigitalSignatureOnlineStatus("signed");
                return;
            } catch (error) {
                if (attempt < maxAttempts) {
                    await delay(delayMs);
                } else {
                    setDigitalSignatureOnlineStatus("failed");
                }
            }
        }
    }

    async function handleDigitalSignatureVerify() {
        if (!idChange) return;

        const signEnvelopeId = searchParams.get("envelopeId");
        const signEvent = searchParams.get("event");

        if (signEnvelopeId && signEvent === "success") {
            trySignWithRetries(idChange, signEnvelopeId); // Start async retry logic (non-blocking)
        }

        if (signEnvelopeId || signEvent) {
            const updatedParams = new URLSearchParams(searchParams);
            updatedParams.delete("envelopeId");
            updatedParams.delete("event");
            setSearchParams(updatedParams);
        }
    }

    return {
        digisignEligibilityData,
        checkDigisignEligibility,
        isDigisignEligibilityLoading,

        signDocument,
        isSigning,
        isSignInitializationFailed,

        handleDigitalSignatureVerify,
        digitalSignatureOnlineStatus,
    };
}
